import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Plus, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "@/components/ui/drawer";

import { PRIMARY_CATEGORIES, ADDITIONAL_CATEGORIES, isAdditionalCategory, getCategory } from "@/lib/categories";
import { createEvent, getEventById, updateEvent, deleteEvent, getAllEvents, bulkCreateEvents } from "@/lib/storage";
import { datesInRangeForWeekdays } from "@/lib/recurrence";
import {
  todayISO,
  toISODate,
  parseISODate,
  formatShortDate,
  addDaysISO,
  pad2,
  toMinutes,
} from "@/lib/time";
import { getLocale } from "@/i18n/locale";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

// Weekday pattern order (Mon → Sun) using getDay() indices (0=Sun..6=Sat).
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const weekdayShort = (idx) =>
  new Intl.DateTimeFormat(getLocale(), { weekday: "short" }).format(new Date(2023, 0, 1 + idx));

function newGroupId() {
  return (
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `grp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  );
}

export default function CreateChronicle() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { date: dateParam, id: editId } = useParams();
  const existing = editId ? getEventById(editId) : null;
  const isEdit = !!existing;

  const [title, setTitle] = useState(existing?.title || "");
  const [date, setDate] = useState(existing?.date || dateParam || todayISO());
  const [startHour, setStartHour] = useState(existing?.startHour ?? 9);
  const [startMinute, setStartMinute] = useState(existing?.startMinute ?? 0);
  const [endHour, setEndHour] = useState(existing?.endHour ?? 10);
  const [endMinute, setEndMinute] = useState(existing?.endMinute ?? 0);
  const [description, setDescription] = useState(existing?.description || "");
  const [priority, setPriority] = useState(existing?.priority ?? 3);
  const [category, setCategory] = useState(existing?.category || "work");
  const [crossMidnight, setCrossMidnight] = useState(existing?.crossMidnight || false);
  const [partOfEnabled, setPartOfEnabled] = useState(!!existing?.parentEventId);
  const [parentEventId, setParentEventId] = useState(existing?.parentEventId || "");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [addlSheetOpen, setAddlSheetOpen] = useState(false);

  // --- Package D: multi-date / weekday-pattern (create mode only) ----------
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatMode, setRepeatMode] = useState("pick"); // "pick" | "weekly"
  const [selectedDates, setSelectedDates] = useState([]); // ISO strings
  const [weekdays, setWeekdays] = useState([]); // getDay() indices
  const [rangeStart, setRangeStart] = useState(date);
  const [rangeEnd, setRangeEnd] = useState(addDaysISO(date, 27));
  const [rangeStartOpen, setRangeStartOpen] = useState(false);
  const [rangeEndOpen, setRangeEndOpen] = useState(false);

  const toggleWeekday = (idx) =>
    setWeekdays((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]));

  // Concrete target dates the save will write to.
  const targetDates = useMemo(() => {
    if (isEdit || !repeatEnabled) return [date];
    if (repeatMode === "pick") return [...selectedDates].sort();
    if (!weekdays.length || !rangeStart || !rangeEnd || rangeEnd < rangeStart) return [];
    return datesInRangeForWeekdays(rangeStart, rangeEnd, new Set(weekdays));
  }, [isEdit, repeatEnabled, repeatMode, selectedDates, weekdays, rangeStart, rangeEnd, date]);

  // Candidate parent events: other events on the same day that are NOT already
  // a child themselves (keeps the relationship a single level — no grandchildren).
  const parentCandidates = useMemo(
    () =>
      getAllEvents()
        .filter((e) => e.date === date && e.id !== editId && !e.parentEventId)
        .sort((a, b) => toMinutes(a.startHour, a.startMinute) - toMinutes(b.startHour, b.startMinute)),
    [date, editId]
  );

  const startMin = toMinutes(startHour, startMinute);
  const endMin = toMinutes(endHour, endMinute);

  const errors = useMemo(() => {
    const errs = {};
    if (!title.trim()) errs.title = t("createEvent.errAddName");
    if (!crossMidnight && endMin <= startMin) errs.time = t("createEvent.errEndAfterStart");
    return errs;
  }, [title, startMin, endMin, crossMidnight, t]);

  const isValid = Object.keys(errors).length === 0;

  const onSave = () => {
    if (!isValid) {
      toast.error(errors.title || errors.time || t("createEvent.errReview"));
      return;
    }
    const cat = getCategory(category);
    const base = {
      title: title.trim(),
      startHour: Number(startHour),
      startMinute: Number(startMinute),
      endHour: Number(endHour),
      endMinute: Number(endMinute),
      description: description.trim(),
      priority: Number(priority),
      category,
      color: cat.hex,
      crossMidnight,
    };

    // Edit mode: single occurrence, unchanged behaviour (never mutates a group).
    if (isEdit) {
      updateEvent(editId, {
        ...base,
        date,
        parentEventId: partOfEnabled ? parentEventId || null : null,
      });
      toast.success(t("createEvent.toastUpdated"));
      navigate(`/calendar/${date}`);
      return;
    }

    // Create mode, single date (repeat off): existing createEvent path.
    if (!repeatEnabled) {
      createEvent({ ...base, date, parentEventId: partOfEnabled ? parentEventId || null : null });
      toast.success(t("createEvent.toastSaved"));
      navigate(`/calendar/${date}`);
      return;
    }

    // Create mode, multi-date / weekday pattern.
    const dates = targetDates;
    if (dates.length === 0) {
      toast.error(t("createEvent.repeatNoDates"));
      return;
    }
    if (dates.length === 1) {
      createEvent({ ...base, date: dates[0], parentEventId: null });
      toast.success(t("createEvent.toastSaved"));
      navigate(`/calendar/${dates[0]}`);
      return;
    }

    // 2+ dates: one bulk write, shared group id (supports future series edit/delete).
    const groupId = newGroupId();
    const drafts = dates.map((d) => ({
      ...base,
      date: d,
      parentEventId: null,
      recurrenceGroupId: groupId,
      bulkJobId: groupId,
      sourceEventId: `${groupId}:${d}`,
    }));
    const { created } = bulkCreateEvents(drafts);
    navigate(`/calendar/${dates[0]}`);
    if (created >= dates.length) {
      toast.success(t("createEvent.toastMultiCreated", { title: base.title, count: dates.length }));
    } else {
      toast.error(
        t("createEvent.toastMultiPartial", {
          created,
          total: dates.length,
          failed: dates.length - created,
        })
      );
    }
  };

  const onDelete = () => {
    if (!isEdit) return;
    deleteEvent(editId);
    toast.success(t("createEvent.toastDeleted"));
    navigate(`/calendar/${date}`);
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-2xl px-4 sm:px-5 py-3 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100"
            data-testid="create-back"
            aria-label={t("createEvent.cancel")}
          >
            <ArrowLeft className="h-4 w-4 text-slate-700" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{isEdit ? t("createEvent.overlineEdit") : t("createEvent.overlineNew")}</p>
            <p className="font-display text-base font-semibold text-slate-900">{isEdit ? t("createEvent.headerEdit") : t("createEvent.headerNew")}</p>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-2xl px-4 sm:px-5 py-6 pb-32">
        <div className="space-y-6">
          {/* Title */}
          <Field label={t("createEvent.eventName")} htmlFor="title" required>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("createEvent.eventNamePlaceholder")}
              className="rounded-xl h-12"
              data-testid="create-title-input"
            />
          </Field>

          {/* Date (single) — hidden when repeating in create mode */}
          {!(repeatEnabled && !isEdit) ? (
            <Field label={t("createEvent.date")}>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="w-full text-left rounded-xl h-12 px-3.5 bg-white border border-input hover:bg-slate-50 transition flex items-center justify-between"
                    data-testid="create-date-trigger"
                  >
                    <span className="text-sm text-slate-900">{formatShortDate(date)}</span>
                    <span className="text-xs font-mono-tab text-slate-500">{date}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto rounded-2xl">
                  <CalendarPicker
                    mode="single"
                    selected={parseISODate(date)}
                    onSelect={(d) => {
                      if (d) {
                        setDate(toISODate(d));
                        setDatePickerOpen(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
          ) : null}

          {/* Repeat on multiple dates (create mode only) */}
          {!isEdit ? (
            <div className="space-y-3">
              <div
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-white px-4 py-3"
                data-testid="create-repeat-row"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{t("createEvent.repeatMultiple")}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t("createEvent.repeatMultipleHint")}</p>
                </div>
                <Switch
                  checked={repeatEnabled}
                  onCheckedChange={setRepeatEnabled}
                  data-testid="create-repeat-toggle"
                />
              </div>

              {repeatEnabled ? (
                <div className="rounded-xl border border-border bg-white p-4 space-y-4" data-testid="create-repeat-panel">
                  {/* Mode segmented control */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "pick", label: t("createEvent.repeatModePick") },
                      { id: "weekly", label: t("createEvent.repeatModeWeekly") },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setRepeatMode(m.id)}
                        className={[
                          "rounded-lg border px-3 py-2 text-sm font-medium transition",
                          repeatMode === m.id
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-border text-slate-700 hover:border-slate-400",
                        ].join(" ")}
                        data-testid={`create-repeat-mode-${m.id}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {repeatMode === "pick" ? (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500">{t("createEvent.repeatPickHint")}</p>
                      <div className="flex justify-center rounded-xl border border-border bg-slate-50 p-1" data-testid="create-repeat-calendar">
                        <CalendarPicker
                          mode="multiple"
                          selected={selectedDates.map(parseISODate)}
                          onSelect={(days) =>
                            setSelectedDates(Array.isArray(days) ? days.map(toISODate).sort() : [])
                          }
                          initialFocus
                        />
                      </div>
                      <p className="text-xs font-medium text-slate-700" data-testid="create-repeat-count">
                        {t("createEvent.repeatSelectedCount", { count: selectedDates.length })}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Weekday chips */}
                      <div className="space-y-2">
                        <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
                          {t("createEvent.repeatWeekdaysLabel")}
                        </Label>
                        <div className="flex flex-wrap gap-2" data-testid="create-repeat-weekdays">
                          {WEEKDAY_ORDER.map((idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => toggleWeekday(idx)}
                              className={[
                                "rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition",
                                weekdays.includes(idx)
                                  ? "border-slate-900 bg-slate-900 text-white"
                                  : "border-border text-slate-700 hover:border-slate-400",
                              ].join(" ")}
                              data-testid={`create-repeat-weekday-${idx}`}
                            >
                              {weekdayShort(idx)}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setWeekdays([1, 2, 3, 4, 5])}
                          className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
                          data-testid="create-repeat-weekdays-monfri"
                        >
                          {t("createEvent.repeatWeekdaysMonFri")}
                        </button>
                      </div>
                      {/* Date range */}
                      <div className="grid grid-cols-2 gap-3">
                        <RangeDate
                          label={t("createEvent.repeatFrom")}
                          value={rangeStart}
                          onChange={setRangeStart}
                          open={rangeStartOpen}
                          setOpen={setRangeStartOpen}
                          testid="create-repeat-from"
                        />
                        <RangeDate
                          label={t("createEvent.repeatTo")}
                          value={rangeEnd}
                          onChange={setRangeEnd}
                          open={rangeEndOpen}
                          setOpen={setRangeEndOpen}
                          testid="create-repeat-to"
                        />
                      </div>
                      <p className="text-xs font-medium text-slate-700" data-testid="create-repeat-count">
                        {t("createEvent.repeatSelectedCount", { count: targetDates.length })}
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}


          {/* Time block */}
          <div className="grid gap-4 sm:grid-cols-2">
            <TimeGroup
              label={t("createEvent.starts")}
              hour={startHour}
              minute={startMinute}
              onHour={setStartHour}
              onMinute={setStartMinute}
              testidPrefix="create-start"
            />
            <TimeGroup
              label={t("createEvent.ends")}
              hour={endHour}
              minute={endMinute}
              onHour={setEndHour}
              onMinute={setEndMinute}
              testidPrefix="create-end"
            />
          </div>
          {errors.time ? (
            <p className="text-xs text-rose-600 -mt-3" data-testid="create-time-error">
              {errors.time}
            </p>
          ) : null}

          {/* Cross-midnight toggle */}
          <div
            className="flex items-center justify-between gap-4 rounded-xl border border-border bg-white px-4 py-3"
            data-testid="create-cross-midnight-row"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">{t("createEvent.crossMidnight")}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t("createEvent.crossMidnightHint")}</p>
            </div>
            <Switch
              checked={crossMidnight}
              onCheckedChange={setCrossMidnight}
              data-testid="create-cross-midnight-toggle"
            />
          </div>

          {/* Part of another event (parent relationship) — toggle-gated.
              Hidden when repeating in create mode (a parent is single-day). */}
          {!(repeatEnabled && !isEdit) ? (
          <div className="space-y-3">
            <div
              className="flex items-center justify-between gap-4 rounded-xl border border-border bg-white px-4 py-3"
              data-testid="create-part-of-row"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{t("createEvent.partOfEvent")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t("createEvent.partOfEventHint")}</p>
              </div>
              <Switch
                checked={partOfEnabled}
                onCheckedChange={(on) => {
                  setPartOfEnabled(on);
                  if (!on) setParentEventId("");
                }}
                data-testid="create-part-of-toggle"
              />
            </div>

            {partOfEnabled ? (
              <Field label={t("createEvent.parentEventLabel")}>
                <Select
                  value={parentEventId || "none"}
                  onValueChange={(v) => setParentEventId(v === "none" ? "" : v)}
                >
                  <SelectTrigger className="rounded-xl h-12" data-testid="create-parent-trigger">
                    <SelectValue placeholder={t("createEvent.partOfEventNone")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="none" data-testid="create-parent-none">
                      {t("createEvent.partOfEventNone")}
                    </SelectItem>
                    {parentCandidates.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`create-parent-${e.id}`}>
                        {e.title} · {pad2(e.startHour)}:{pad2(e.startMinute)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {parentCandidates.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-1.5" data-testid="create-parent-empty">
                    {t("createEvent.partOfEventEmpty")}
                  </p>
                ) : null}
              </Field>
            ) : null}
          </div>
          ) : null}

          {/* Description */}
          <Field label={t("createEvent.descriptionOptional")}>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("createEvent.descriptionPlaceholder")}
              rows={3}
              className="rounded-xl"
              data-testid="create-description"
            />
          </Field>

          {/* Priority */}
          <Field label={t("createEvent.priority")}>
            <Select value={String(priority)} onValueChange={(v) => setPriority(Number(v))}>
              <SelectTrigger className="rounded-xl h-12" data-testid="create-priority-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[5, 4, 3, 2, 1].map((p) => (
                  <SelectItem key={p} value={String(p)} data-testid={`create-priority-${p}`}>
                    {t(`createEvent.priorities.${p}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Category */}
          <Field label={t("createEvent.category")}>
            <div className="grid grid-cols-3 gap-2" data-testid="create-category-grid">
              {PRIMARY_CATEGORIES.map((c) => {
                const active = c.id === category;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={[
                      "relative rounded-xl border p-3 text-left transition",
                      active
                        ? "border-slate-900 ring-2 ring-slate-900/10"
                        : "border-border hover:border-slate-400",
                    ].join(" ")}
                    data-testid={`create-category-${c.id}`}
                  >
                    <span
                      className="block h-6 w-full rounded-md border border-black/5"
                      style={{ backgroundColor: c.hex }}
                    />
                    <span className="mt-2 block text-[12px] font-medium text-slate-800 leading-tight">
                      {t(`categories.${c.id}`)}
                    </span>
                    {active ? (
                      <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* Additional categories — secondary bottom-sheet trigger */}
            {(() => {
              const addlSelected = isAdditionalCategory(category);
              const cat = getCategory(category);
              return (
                <button
                  type="button"
                  onClick={() => setAddlSheetOpen(true)}
                  className={[
                    "mt-2 w-full flex items-center gap-2 rounded-xl border px-3 py-3 text-left transition",
                    addlSelected
                      ? "border-slate-900 ring-2 ring-slate-900/10"
                      : "border-dashed border-slate-300 hover:border-slate-400",
                  ].join(" ")}
                  data-testid="create-additional-categories-btn"
                >
                  {addlSelected ? (
                    <>
                      <span className="h-6 w-6 rounded-md border border-black/5 shrink-0" style={{ backgroundColor: cat.hex }} />
                      <span className="min-w-0">
                        <span className="block text-[10px] uppercase tracking-wide text-slate-400">{t("createEvent.additionalCategories")}</span>
                        <span className="block text-[13px] font-medium text-slate-800 truncate">{t(`categories.${category}`)}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 text-slate-500 shrink-0" />
                      <span className="text-[13px] font-medium text-slate-600">{t("createEvent.additionalCategories")}</span>
                    </>
                  )}
                </button>
              );
            })()}
          </Field>

          {/* Additional categories bottom sheet (reuses ui/drawer — opens from below) */}
          <Drawer open={addlSheetOpen} onOpenChange={setAddlSheetOpen}>
            <DrawerContent data-testid="additional-categories-sheet">
              <DrawerHeader className="text-left">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <DrawerTitle>{t("createEvent.additionalCategoriesTitle")}</DrawerTitle>
                    <DrawerDescription>{t("createEvent.additionalCategoriesDesc")}</DrawerDescription>
                  </div>
                  <DrawerClose asChild>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-slate-100 shrink-0"
                      aria-label={t("createEvent.cancel")}
                      data-testid="additional-categories-close"
                    >
                      <X className="h-4 w-4 text-slate-600" />
                    </button>
                  </DrawerClose>
                </div>
              </DrawerHeader>
              <div className="px-4 pb-6 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ADDITIONAL_CATEGORIES.map((c) => {
                    const active = c.id === category;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setCategory(c.id); setAddlSheetOpen(false); }}
                        className={[
                          "relative rounded-xl border p-3 text-left transition",
                          active
                            ? "border-slate-900 ring-2 ring-slate-900/10"
                            : "border-border hover:border-slate-400",
                        ].join(" ")}
                        data-testid={`additional-category-${c.id}`}
                      >
                        <span
                          className="block h-6 w-full rounded-md border border-black/5"
                          style={{ backgroundColor: c.hex }}
                        />
                        <span className="mt-2 block text-[12px] font-medium text-slate-800 leading-tight">
                          {t(`categories.${c.id}`)}
                        </span>
                        {active ? (
                          <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center">
                            <Check className="h-3 w-3" />
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </main>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl border-t border-border">
        <div className="mx-auto max-w-2xl px-4 sm:px-5 py-3 flex gap-3">
          {isEdit ? (
            <Button
              variant="outline"
              className="rounded-full h-12 px-4 border-slate-300 text-slate-700 hover:bg-slate-100"
              onClick={onDelete}
              data-testid="edit-delete"
            >
              {t("createEvent.delete")}
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="rounded-full h-12 flex-1 border-slate-300"
            onClick={() => navigate(-1)}
            data-testid="create-cancel"
          >
            {t("createEvent.cancel")}
          </Button>
          <Button
            className="rounded-full h-12 flex-1 bg-slate-900 hover:bg-slate-800"
            onClick={onSave}
            disabled={!isValid}
            data-testid="create-save"
          >
            {isEdit ? t("createEvent.saveChanges") : t("createEvent.saveChronicle")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RangeDate({ label, value, onChange, open, setOpen, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full text-left rounded-xl h-12 px-3.5 bg-white border border-input hover:bg-slate-50 transition flex items-center gap-2"
            data-testid={`${testid}-trigger`}
          >
            <CalendarDays className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="text-sm text-slate-900 truncate">{formatShortDate(value)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto rounded-2xl">
          <CalendarPicker
            mode="single"
            selected={parseISODate(value)}
            onSelect={(d) => {
              if (d) {
                onChange(toISODate(d));
                setOpen(false);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Field({ label, htmlFor, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={htmlFor}
        className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium"
      >
        {label}
        {required ? <span className="text-rose-500 ml-1">*</span> : null}
      </Label>
      {children}
    </div>
  );
}

function TimeGroup({ label, hour, minute, onHour, onMinute, testidPrefix }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Select value={String(hour)} onValueChange={(v) => onHour(Number(v))}>
          <SelectTrigger className="rounded-xl h-12 flex-1 font-mono-tab" data-testid={`${testidPrefix}-hour-trigger`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {HOURS.map((h) => (
              <SelectItem key={h} value={String(h)} data-testid={`${testidPrefix}-hour-${h}`}>
                {pad2(h)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-slate-400 font-mono-tab">:</span>
        <Select value={String(minute)} onValueChange={(v) => onMinute(Number(v))}>
          <SelectTrigger className="rounded-xl h-12 flex-1 font-mono-tab" data-testid={`${testidPrefix}-minute-trigger`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)} data-testid={`${testidPrefix}-minute-${m}`}>
                {pad2(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
